/* eslint-disable camelcase  */

import {
  Card,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TableContainer,
  TablePagination,
  Typography,
  Button,
  MenuItem,
  InputLabel,
  FormControl,
  Select,
  Input,
  TextField,
  ButtonGroup,
  OutlinedInput,
  InputAdornment,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import React, { useEffect, useState, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import DatePicker from 'react-datepicker';
import { AgGridReact } from 'ag-grid-react';
import * as XLSX from 'xlsx';
import ModernAgGrid from '../components/ModernAgGrid';
import Iconify from '../components/Iconify';
import Scrollbar from '../components/Scrollbar';
import Page from '../components/Page';
import useAxiosWithAuth from '../api/useAxiosWithAuth';

function adjustAndFormatDate(startDate) {
  const date = new Date(startDate);
  date.setDate(date.getDate() + 1); // Add 1 day to the date
  return date.toISOString().split('T')[0];
}

function convertToDate(dotNetDate) {
  // Extract the number from the string
  const timestamp = parseInt(dotNetDate.replace(/\/Date\((-?\d+)\)\//, '$1'), 10);

  // Create a new Date object using the timestamp
  const date = new Date(timestamp);

  const options = {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  };
  return date.toLocaleString('en-GB', options);
}

const gridOptions = {
  icons: {
    sortAscending:
      '<span class="custom-sort-icon"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-up-narrow-wide"><path d="m3 8 4-4 4 4"/><path d="M7 4v16"/><path d="M11 12h4"/><path d="M11 16h7"/><path d="M11 20h10"/></svg></span>', // Ascending icon
    sortDescending:
      '<span class="custom-sort-icon"><svg xmlns="http://www.w3.org/2000/svg" width=16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-down-narrow-wide"><path d="m3 16 4 4 4-4"/><path d="M7 20V4"/><path d="M11 4h4"/><path d="M11 8h7"/><path d="M11 12h10"/></svg></span>', // Descending icon
    sortUnSort: '<span class="custom-sort-icon">-</span>', // Default unsorted icon
  },
};

const CustomerValidationReport = () => {
  // Function to handle column header clicks for sorting
  const handleSort = (columnName) => {
    setPage(0); // Reset to first page when sorting changes

    const apiFieldName = columnName;

    // If clicking the same column
    if (sortColumnName === apiFieldName) {
      if (sortType === 'asc') {
        // asc -> desc
        setSortType('desc');
      } else if (sortType === 'desc') {
        // desc -> none (reset)
        setSortColumnName('');
        setSortType('');
      }
    } else {
      // Different column - set to asc
      setSortColumnName(apiFieldName);
      setSortType('asc');
    }
  };

  // Helper function to get sort indicator
  const getSortIndicator = (field) => {
    const apiFieldName = field;

    if (sortColumnName === apiFieldName) {
      return sortType === 'asc' ? '↓' : '↑'; // Down arrow for ascending, up arrow for descending
    }
    return '↕'; // Default icon for unsorted columns
  };

  // Function to convert JSON data to Excel and initiate download with proper column widths
  const exportToExcel = async (post, user, currentApproveStatus, currentStartDate, currentEndDate, filteredData) => {
    setIsExportLoading(true); // Set loading state to true
    try {
      // Use the filtered data for export
      const data = filteredData;

      if (!data || data.length === 0) {
        console.log('No data to export');
        return;
      }

      // Define headers based on the fields in the data
      const headers = [
        'Woo Order ID',
        'Line Item ID',
        'Product Code',
        'Supplier Product Code',
        'Product Description',
        'Product Batch Number',
        'Whole Sale Price',
        'Date Completed',
        'Order Warehouse',
        'Quantity',
        'Pharmacy Name',
        'TGA Number',
        'Prescribing Doctor',
        'Status',
        'Approved Status Changed',
        'Address 1',
        'Address 2',
        'City',
        'State',
        'Postcode',
        'Country',
      ];

      // Prepare the data array with headers as the first row
      const worksheetData = [
        headers,
        ...data.map((item) => [
          item.order_id || '',
          item.line_item_id || '',
          item.product_code || '',
          item.supplier_product_code || '',
          item.product_description || '',
          item.product_batch_number || '',
          item.whole_sale_price || '',
          item.date_completed || '',
          item.order_warehouse || '',
          item.quantity || '',
          item.pharmacy_name || '',
          item.tga_number || '',
          item.prescribing_doctor || '',
          item.is_validated ? 'Approved' : 'Awaiting Approval',
          item.approved_status_changed || '',
          item.address?.address_1 || '',
          item.address?.address_2 || '',
          item.address?.city || '',
          item.address?.state || '',
          item.address?.postcode || '',
          item.address?.country || '',
        ]),
      ];

      // Create a worksheet
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

      // Add styling to the header row
      const headerCellStyle = {
        font: { bold: true, color: { rgb: '000080' } }, // Bold and dark blue font
        fill: { fgColor: { rgb: 'ADD8E6' } }, // Light blue background
      };

      // Apply styling to header cells (first row)
      const range = XLSX.utils.decode_range(worksheet['!ref']);
      for (let C = range.s.c; C <= range.e.c; C += 1) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C }); // Header row is always row 0
        if (!worksheet[cellAddress]) {
          // Skip if cell doesn't exist
        } else {
          // Apply header styling
          worksheet[cellAddress].s = headerCellStyle;
        }
      }

      // Define column widths based on content or predefined widths
      const colWidths = [
        { wch: 15 }, // Woo Order ID
        { wch: 15 }, // Line Item ID
        { wch: 30 }, // Product Code (1.5x wider)
        { wch: 25 }, // Supplier Product Code
        { wch: 60 }, // Product Description (2x wider)
        { wch: 20 }, // Product Batch Number
        { wch: 15 }, // Whole Sale Price
        { wch: 30 }, // Date Completed (wider)
        { wch: 20 }, // Order Warehouse
        { wch: 10 }, // Quantity
        { wch: 37 }, // Pharmacy Name (1.5x wider)
        { wch: 20 }, // TGA Number
        { wch: 25 }, // Prescribing Doctor
        { wch: 20 }, // Status
        { wch: 20 }, // Approved Status Changed
        { wch: 40 }, // Address 1 (wider)
        { wch: 40 }, // Address 2 (wider)
        { wch: 20 }, // City
        { wch: 15 }, // State
        { wch: 15 }, // Postcode
        { wch: 15 }, // Country
      ];

      worksheet['!cols'] = colWidths;

      // Create a workbook and append the worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Customer Validation Report');

      // Generate Excel file and trigger download
      XLSX.writeFile(workbook, `customer_validation_report_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (error) {
      console.error('Error exporting Excel:', error);
    } finally {
      setIsExportLoading(false); // Set loading state to false
    }
  };

  const columnDefs = useMemo(
    () => [
      {
        resizable: true,
        field: 'supplier_product_code',
        headerName: 'Supplier Product Code',
        width: 220,
        sortable: true,
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('supplier_product_code')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('supplier_product_code');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Supplier Product Code</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('supplier_product_code')}</span>
          </div>
        ),
        cellRenderer: (params) => <span style={{ paddingLeft: '15px' }}>{params.value || 'N/A'}</span>,
      },
      {
        resizable: true,
        field: 'order_id',
        headerName: 'Woo Order ID',
        width: 200,
        sortable: true,
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('order_id')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('order_id');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Woo Order ID</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('order_id')}</span>
          </div>
        ),
        cellRenderer: (params) => <span style={{ paddingLeft: '15px' }}>{params.value || 'N/A'}</span>,
      },
      {
        resizable: true,
        field: 'date_completed',
        headerName: 'Date Completed',
        type: 'centerAligned',
        width: 200,
        sortable: true,
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('date_completed')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('date_completed');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Date Completed</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('date_completed')}</span>
          </div>
        ),
        cellRenderer: (params) => (
          <span style={{ paddingLeft: '15px', display: 'block', width: '100%' }}>
            {params.value
              ? `${new Date(params.value).toLocaleDateString()} ${new Date(params.value).toLocaleTimeString()}`
              : 'N/A'}
          </span>
        ),
      },
      {
        resizable: true,
        field: 'order_warehouse',
        headerName: 'Order Warehouse',
        width: 200,
        sortable: true,
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('order_warehouse')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('order_warehouse');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Order Warehouse</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('order_warehouse')}</span>
          </div>
        ),
        cellRenderer: (params) => <span style={{ paddingLeft: '15px' }}>{params.value || 'N/A'}</span>,
      },
      {
        resizable: true,
        field: 'product_code',
        headerName: 'Product Code',
        width: 200,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        pinned: 'left',
        sortable: true,
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('product_code')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('product_code');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Product Code</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('product_code')}</span>
          </div>
        ),
        cellRenderer: (params) => <span style={{ paddingLeft: '15px' }}>{params.value || 'N/A'}</span>,
      },
      {
        resizable: true,
        field: 'pharmacy_name',
        headerName: 'Customer Name',
        width: 200,
        sortable: true,
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('pharmacy_name')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('pharmacy_name');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Customer Name</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('pharmacy_name')}</span>
          </div>
        ),
        cellRenderer: (params) => <span style={{ paddingLeft: '15px' }}>{params.value || 'N/A'}</span>,
      },
      {
        resizable: true,
        field: 'address',
        headerName: 'Customer Address',
        width: 350,
        cellStyle: {
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        },
        sortable: true,
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('address')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('address');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Customer Address</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('address')}</span>
          </div>
        ),
        cellRenderer: (params) => {
          const addressParts = [];
          if (params.value.address_1) addressParts.push(params.value.address_1);
          if (params.value.address_2) addressParts.push(params.value.address_2);
          if (params.value.city || params.value.state || params.value.postcode) {
            const cityStateZip = [params.value.city, params.value.state, params.value.postcode]
              .filter(Boolean)
              .join(' ');
            addressParts.push(cityStateZip);
          }
          if (params.value.country) addressParts.push(params.value.country);

          return <div style={{ paddingLeft: '15px' }}>{addressParts.join(', ')}</div>;
        },
      },
      // {
      //   resizable: true,
      //   field: 'approved_status_changed',
      //   headerName: 'Status Change Date',
      //   width: 200,
      //   cellRenderer: (params) => (
      //     <span style={{ paddingLeft: '15px', display: 'block', width: '100%' }}>
      //       {params.value ? params.value.split(' ')[0] : 'N/A'}
      //     </span>
      //   ),
      // },
      {
        resizable: true,
        field: 'product_description',
        headerName: 'Product Description',
        width: 250,
        sortable: true,
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('product_description')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('product_description');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Product Description</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('product_description')}</span>
          </div>
        ),
        cellRenderer: (params) => <span style={{ paddingLeft: '15px' }}>{params.value || 'N/A'}</span>,
      },
      // {
      //   resizable: true,
      //   field: 'is_validated',
      //   headerName: 'Status',
      //   width: 200,
      //   cellRenderer: (params) => (
      //     <span style={{ paddingLeft: '15px' }}>{params.value ? 'Approved' : 'Not Approved'}</span>
      //   ),
      // },

      {
        resizable: true,
        field: 'quantity',
        headerName: 'Qty of Sold Products',
        width: 200,
        sortable: true,
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('quantity')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('quantity');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Qty of Sold Products</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('quantity')}</span>
          </div>
        ),
        cellRenderer: (params) => (
          <span style={{ paddingLeft: '15px', display: 'block', width: '100%', textAlign: 'center' }}>
            {params.value || 'N/A'}
          </span>
        ),
      },
      {
        resizable: true,
        field: 'whole_sale_price',
        headerName: 'Whole Sale Price',
        width: 200,
        sortable: true,
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('whole_sale_price')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('whole_sale_price');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Whole Sale Price</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('whole_sale_price')}</span>
          </div>
        ),
        cellRenderer: (params) => (
          <span style={{ paddingLeft: '15px', display: 'block', textAlign: 'center' }}>
            {params.value.toFixed(2) || 'N/A'}
          </span>
        ),
      },
      {
        resizable: true,
        field: 'product_batch_number',
        headerName: 'Batch No',
        width: 200,
        sortable: true,
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('product_batch_number')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('product_batch_number');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Batch No</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('product_batch_number')}</span>
          </div>
        ),
        cellRenderer: (params) => <span style={{ paddingLeft: '15px' }}>{params.value || 'N/A'}</span>,
      },
      {
        resizable: true,
        field: 'prescribing_doctor',
        headerName: 'Prescribing Doctor',
        width: 200,
        sortable: true,
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('prescribing_doctor')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('prescribing_doctor');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>Prescribing Doctor</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('prescribing_doctor')}</span>
          </div>
        ),
        cellRenderer: (params) => <span style={{ paddingLeft: '15px' }}>{params.value || 'N/A'}</span>,
      },
      {
        resizable: true,
        field: 'tga_number',
        headerName: 'TGA Approval Number',
        width: 300,
        sortable: true,
        headerComponent: (params) => (
          <div
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => handleSort('tga_number')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSort('tga_number');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span style={{ flex: 1 }}>TGA Approval Number</span>
            <span style={{ marginLeft: '4px' }}>{getSortIndicator('tga_number')}</span>
          </div>
        ),
        cellRenderer: (params) => <span style={{ paddingLeft: '15px' }}>{params.value || 'N/A'}</span>,
      },
      {
        resizable: true,
        field: 'action',
        headerName: 'Action',
        width: 120,
        pinned: 'right',
        cellStyle: {
          borderLeft: '1px solid #e0e0e0',
        },
        cellRenderer: (params) => (
          <Stack sx={{ padding: '0px 10px' }} direction={'row'} gap={2} justifyContent={'start'} alignItems={'center'}>
            {!params.data.is_validated ? (
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  setActionType('approve');
                  setSelectedRows([params.data]);
                  setOpenDialog(true);
                }}
              >
                Approve
              </Button>
            ) : (
              <Button
                variant="contained"
                size="small"
                // onClick={() => {
                //   setActionType('disapprove');
                //   setSelectedRows([params.data]);
                //   setOpenDialog(true);
                // }}
                disabled
              >
                Approved
              </Button>
            )}
          </Stack>
        ),
      },
    ],
    [handleSort, getSortIndicator]
  );

  const { post, put, get } = useAxiosWithAuth();
  const [allData, setAllData] = useState([]); // Store all data locally
  const [filteredData, setFilteredData] = useState([]); // Store filtered data
  const [displayData, setDisplayData] = useState([]); // Store data to display after pagination
  const [originalTotal, setOriginalTotal] = useState(0); // Store original total from API

  const maxDate = new Date().toISOString().split('T')[0];

  const date = new Date().toISOString().slice(0, 7);
  // const firstDayOfCurrentMonth = new Date(date.getFullYear(), date.getMonth(), 1, 12);
  // const lastDayOfCurrentMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0, 12);

  // const formattedFirstDate = firstDayOfCurrentMonth.toISOString().slice(0, 10);
  // const formattedLastDate = lastDayOfCurrentMonth.toISOString().slice(0, 10);

  const [reportDate, setReportDate] = useState(date);
  const [reportEndDate, setReportEndDate] = useState(date);
  const [isLoading, setIsLoading] = useState(true);
  const [isBatchLoading, setIsBatchLoading] = useState(true);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [shouldRefresh, setShouldRefresh] = useState(false);

  const [selectedRows, setSelectedRows] = useState([]);

  const [selectedProductCode, setSelectedProductCode] = useState('');
  const [currentProductCode, setCurrentProductCode] = useState('');

  const [openDialog, setOpenDialog] = React.useState(false);
  const [openBatchDialog, setOpenBatchDialog] = React.useState(false);
  const [reason, setReason] = useState('');

  const [batchInfo, setBatchInfo] = React.useState([]);
  const [batchError, setBatchError] = React.useState(false);

  const [actionType, setActionType] = React.useState('approve');

  const [approveStatus, setApproveStatus] = React.useState(3);

  const [isReject, setIsReject] = useState(false);

  const [errorMessage, setErrorMessage] = useState('');

  const [searchValue, setSearchValue] = useState('');
  const [filterValue, setFilterValue] = useState(''); // For the actual API filter
  const [sortColumnName, setSortColumnName] = useState('');
  const [sortType, setSortType] = useState(''); // 'asc', 'desc', or ''

  // date range

  const [startDate, setStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [endDate, setEndDate] = useState(new Date());

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const formatDate = (date) => {
    if (!date) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleChange = (event) => {
    setApproveStatus(event.target.value);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setIsReject(false);
    setReason('');
    setErrorMessage('');
  };

  const { user } = useAuth0();

  const approveStatusApi = async (payload) => {
    try {
      setIsLoading(true);
      const res = await post(`product/approve-or-reject-lineitem`, payload);
      // console.log({ payload });
      setIsLoading(false);
    } catch (err) {
      console.log(err);
      setIsLoading(false);
    } finally {
      setShouldRefresh(true);
      setOpenDialog(false);
      setSelectedRows([]);
      setReason('');
      setErrorMessage('');
    }
  };

  const disapproveStatusApi = async (payload) => {
    try {
      setIsLoading(true);
      // const res = await post(`product/invalidate-orders`, { modify_orders: payload });
      console.log({ payload });
      setIsLoading(false);
    } catch (err) {
      console.log(err);
      setIsLoading(false);
    } finally {
      setShouldRefresh(true);
      setOpenDialog(false);
      setSelectedRows([]);
    }
  };

  const handleDialogAction = () => {
    const payload = selectedRows.map((row) => ({
      order_id: row.order_id,
      line_id: row.line_item_id,
      supplier_code: user['http://healthhouse.com/supplier_code'],
    }));
    console.log({ actionType, payload });
    if (actionType === 'approve') {
      approveStatusApi({ modify_orders: payload, validate: true });
    } else {
      if (!reason) {
        setErrorMessage('Reject reason is required!');
        return;
      }
      setErrorMessage('');
      approveStatusApi({ modify_orders: payload, validate: false, message: reason });
    }
    setIsReject(false);
    setActionType('approve');
  };

  useEffect(() => {
    const getReport = async () => {
      setIsLoading(true);
      try {
        // Fetch all data in one call with a large page size
        const allDataRes = await post(
          `product/customer-validation-report/${user['http://healthhouse.com/supplier_code']}?page_size=99999999&page_number=0`, // page_number is 1-indexed
          {
            approve_status: 1, // Get all records regardless of approval status (1 = All)
            start_date: adjustAndFormatDate(startDate),
            end_date: adjustAndFormatDate(endDate),
            search_key: '', // Don't apply search filter since it's handled locally
          }
        );

        const totalSize = allDataRes.data.meta.total_size;
        setTotal(totalSize); // Set the total to the count from API
        setOriginalTotal(totalSize); // Store original total

        setAllData(allDataRes.data.payload);
        setFilteredData(allDataRes.data.payload); // Initialize filtered data
      } catch (err) {
        console.log(err);
        setAllData([]);
        setFilteredData([]);
        setOriginalTotal(0);
        setTotal(0);
      } finally {
        setShouldRefresh(false);
        setIsLoading(false);
      }
    };

    if (startDate !== null && endDate !== null) {
      getReport();
    }
  }, [user, shouldRefresh, startDate, endDate]);

  // New useEffect to handle local sorting and pagination
  useEffect(() => {
    if (!allData || allData.length === 0) {
      setFilteredData([]);
      return;
    }

    let result = [...allData];

    // Apply approval status filter locally
    if (approveStatus !== 1) {
      // 1 = All, 2 = Approved Only, 3 = AWaiting Approval Only
      if (approveStatus === 2) {
        // Approved Only
        result = result.filter((item) => item.is_validated === true);
      } else if (approveStatus === 3) {
        // Awaiting Approval Only
        result = result.filter((item) => item.is_validated === false);
      }
    }

    // Apply search filter locally
    if (filterValue) {
      const searchLower = filterValue.toLowerCase();
      result = result.filter(
        (item) =>
          item.product_code?.toLowerCase().includes(searchLower) ||
          item.product_description?.toLowerCase().includes(searchLower)
      );
    }

    // Apply sorting
    if (sortColumnName && sortType) {
      result = result.sort((a, b) => {
        let valueA = a[sortColumnName];
        let valueB = b[sortColumnName];

        // Handle nested properties
        if (sortColumnName === 'address') {
          // For address, we need to create a string representation
          const addressA = a[sortColumnName];
          const addressB = b[sortColumnName];

          const partsA = [];
          if (addressA?.address_1) partsA.push(addressA.address_1);
          if (addressA?.address_2) partsA.push(addressA.address_2);
          if (addressA?.city || addressA?.state || addressA?.postcode) {
            partsA.push([addressA.city, addressA.state, addressA.postcode].filter(Boolean).join(' '));
          }
          if (addressA?.country) partsA.push(addressA.country);

          const partsB = [];
          if (addressB?.address_1) partsB.push(addressB.address_1);
          if (addressB?.address_2) partsB.push(addressB.address_2);
          if (addressB?.city || addressB?.state || addressB?.postcode) {
            partsB.push([addressB.city, addressB.state, addressB.postcode].filter(Boolean).join(' '));
          }
          if (addressB?.country) partsB.push(addressB.country);

          valueA = partsA.join(', ');
          valueB = partsB.join(', ');
        }

        // Handle different data types
        if (valueA === null || valueA === undefined) valueA = '';
        if (valueB === null || valueB === undefined) valueB = '';

        // Convert to appropriate types for comparison
        if (typeof valueA === 'string' && typeof valueB === 'string') {
          valueA = valueA.toLowerCase();
          valueB = valueB.toLowerCase();
        } else if (typeof valueA === 'number' && typeof valueB === 'number') {
          // Keep as numbers for comparison
        } else if (valueA instanceof Date && valueB instanceof Date) {
          // Keep as dates for comparison
        } else {
          // Convert to string for comparison
          valueA = String(valueA);
          valueB = String(valueB);
        }

        if (sortType === 'asc') {
          if (valueA < valueB) return -1;
          if (valueA > valueB) return 1;
          return 0;
        }

        // desc
        if (valueA > valueB) return -1;
        if (valueA < valueB) return 1;
        return 0;
      });
    }

    setFilteredData(result);
  }, [allData, approveStatus, filterValue, sortColumnName, sortType]);

  // New useEffect to handle pagination
  useEffect(() => {
    if (!filteredData || filteredData.length === 0) {
      setDisplayData([]);
      setTotal(0);
      return;
    }

    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    const paginatedData = filteredData.slice(start, end);
    setDisplayData(paginatedData);
    // Use the filtered data length for total (this reflects current filtering)
    setTotal(filteredData.length);
  }, [filteredData, page, rowsPerPage]);

  useEffect(() => {
    const batchInfo = async () => {
      setIsBatchLoading(true);
      try {
        const res = await get(`batch/batches-by-sku/${encodeURIComponent(selectedProductCode)}`);
        if (res.data && res.data.payload) {
          setBatchInfo(res.data.payload);
        } else {
          setBatchInfo([]);
          setBatchError(true);
        }
        setBatchInfo(res.data.payload);
        setIsBatchLoading(false);
      } catch (err) {
        console.log(err);
        setBatchInfo([]);
        setBatchError(true);
        setIsBatchLoading(false);
      } finally {
        setIsBatchLoading(false);
      }
    };

    batchInfo();
  }, [user, selectedProductCode]);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  return (
    <Page title="Stock Report">
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} ml={2}>
          <Typography variant="h4" gutterBottom>
            Customer Validation Report
          </Typography>
          {selectedRows.length > 0 && (
            <Stack direction={'row'} gap={2}>
              <Button
                variant="contained"
                onClick={() => {
                  setOpenDialog(true);
                  setActionType('approve');
                }}
              >
                Approve All
              </Button>
              {/* <Button
                variant="contained"
                onClick={() => {
                  setOpenDialog(true);
                  setActionType('disapprove');
                }}
              >
                Disapprove All
              </Button> */}
            </Stack>
          )}
        </Stack>
        <Stack justifyContent={'flex-end'} sx={{ my: 3 }} direction={'row'} alignItems={'center'} gap={2}>
          {searchValue && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setSearchValue('');
                setFilterValue('');
                setPage(0); // Reset to first page when clearing search
              }}
            >
              Clear Search
            </Button>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setFilterValue(searchValue);
              setPage(0); // Reset to first page when searching
            }}
          >
            <OutlinedInput
              size="small"
              placeholder="Search..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              endAdornment={
                <InputAdornment position="end">
                  <button type="submit" style={{ border: 'none', background: 'none', cursor: 'pointer' }}>
                    <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled', width: 20, height: 20 }} />
                  </button>
                </InputAdornment>
              }
              sx={{ minWidth: 200 }}
            />
          </form>
          <FormControl sx={{ width: 200 }} size="small">
            <InputLabel id="demo-simple-select-label">Approval Status</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={approveStatus}
              label="Approve Status"
              onChange={handleChange}
            >
              <MenuItem value={1}>All</MenuItem>
              <MenuItem value={2}>Approved Only</MenuItem>
              <MenuItem value={3}>Awaiting Approval Only</MenuItem>
            </Select>
          </FormControl>
          <DatePicker
            selected={startDate}
            onChange={handleDateChange}
            startDate={startDate}
            endDate={endDate}
            selectsRange
            dateFormat="dd/MM/yyyy"
            customInput={
              <TextField
                label="Select Range"
                size="small"
                variant="outlined"
                className="w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Select Range"
                value={formatDate(startDate)}
                readOnly
              />
            }
          />
          <Button
            variant="contained"
            onClick={() => exportToExcel(post, user, approveStatus, startDate, endDate, filteredData)}
            disabled={isExportLoading}
          >
            {isExportLoading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Exporting...
              </>
            ) : (
              'Export Excel'
            )}
          </Button>
        </Stack>
        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800, maxHeight: 550, p: 3 }}>
              <div className="ag-theme-material" style={{ height: 500, minWidth: 750 }}>
                {isLoading ? (
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      flexDirection: 'column',
                    }}
                  >
                    <CircularProgress size={72} sx={{ mb: 3 }} />
                    <h5>Please wait while we prepare your data</h5>
                  </div>
                ) : (
                  <ModernAgGrid
                    suppressLoadingOverlay
                    rowData={displayData}
                    // rowData={demoData}
                    columnDefs={columnDefs}
                    rowSelection="multiple"
                    onSelectionChanged={(e) => {
                      setSelectedRows(e.api.getSelectedRows());
                    }}
                    isRowSelectable={(rowNode) => !rowNode?.data?.is_validated}
                    onSortChanged={(params) => {
                      console.log({ params });
                    }}
                    gridOptions={gridOptions}
                    style={{ height: 520, minWidth: 750 }}
                  />
                )}
              </div>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Change Status?</DialogTitle>
        <DialogContent>
          <DialogContentText>Please be absolutely sure before changing the status.</DialogContentText>
          <ButtonGroup fullWidth variant="contained" aria-label="Basic button group" style={{ marginTop: '16px' }}>
            <Button
              // color="success"
              disabled={isReject}
              onClick={() => {
                setActionType('approve');
                handleDialogAction();
              }}
            >
              Accept
            </Button>
            {/* <Button
              color="error"
              onClick={() => {
                setActionType('disapprove');
                setIsReject(true);
              }}
              disabled={isReject}
            >
              Reject
            </Button> */}
          </ButtonGroup>
          {isReject && (
            <div>
              <TextField
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                multiline
                rows={3}
                fullWidth
                style={{ marginTop: '16px' }}
                label="Reject Reason"
                error={!!errorMessage}
                helperText={errorMessage}
              />
              <div
                style={{ marginTop: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <Button
                  color="error"
                  variant="outlined"
                  onClick={() => {
                    setIsReject(false);
                    setActionType('approve');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  color="error"
                  variant="contained"
                  style={{ marginTop: '16px' }}
                  onClick={() => {
                    handleDialogAction();
                  }}
                >
                  Submit
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          {/* <Button onClick={handleCloseDialog}>Cancel</Button> */}
          {/* <Button onClick={handleDialogAction}>Update</Button> */}
        </DialogActions>
      </Dialog>
      {/* batch dialog */}
      <Dialog maxWidth={'xl'} open={openBatchDialog} onClose={handleCloseDialog}>
        <DialogTitle>Batch Details</DialogTitle>
        <DialogContent>
          {isBatchLoading && (
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
              }}
            >
              <CircularProgress size={72} sx={{ mb: 3 }} />
              <h5>Please wait while we prepare your data</h5>
            </div>
          )}
          {!isBatchLoading && batchError && (
            <div
              style={{
                width: '100%',
                height: '200px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
              }}
            >
              <Iconify icon={'mdi:alert-circle-outline'} width={64} height={64} color="#FFA48D" />
              <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
                No Batch Data Found
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                Unable to fetch batch details. Please try again later.
              </Typography>
            </div>
          )}
          {!isBatchLoading && !batchError && batchInfo?.length === 0 && (
            <div
              style={{
                width: '100%',
                height: '200px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
              }}
            >
              <Iconify icon={'mdi:database-remove-outline'} width={64} height={64} color="#919EAB" />
              <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
                No Data Available
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                No batch records found for this product.
              </Typography>
            </div>
          )}
          {!isBatchLoading && !batchError && batchInfo?.length > 0 && (
            <TableContainer sx={{ minWidth: 800 }}>
              <Table aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell align="left">Batch</TableCell>
                    <TableCell align="center">Expiry Date</TableCell>
                    <TableCell align="center">Quantity</TableCell>
                    <TableCell align="right">Warehouse Code</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {batchInfo?.map((item, index) => (
                    <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell align="left">{item?.Number}</TableCell>
                      <TableCell align="center">{convertToDate(item?.ExpiryDate)}</TableCell>
                      <TableCell align="center">{item?.Quantity}</TableCell>
                      <TableCell align="right">{item?.WarehouseCode}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
};

export default CustomerValidationReport;
